/**
 * Insurance Report State Management Hook
 * Manages the multi-step workflow for creating insurance summaries
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { copilotApi } from '../services/copilotApi';
import type {
  GetReportResponse,
  GetReportResponseCompleted,
  GetReportResponseError,
  OutputFormat,
  PDFRecord,
  ReportStatus,
} from '../types/insurance';

export interface PDFUploadState {
  file?: File;
  filename: string;
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'error';
  progress: number;
  pdfId?: string;
  findingsExtracted?: number;
  error?: string;
}

export interface UseInsuranceReportReturn {
  // State
  reportId: string | null;
  currentStep: number;
  uploadedPDFs: PDFUploadState[];
  reportStatus: ReportStatus | null;
  results: GetReportResponseCompleted | null;
  error: string | null;
  isInitializing: boolean;

  // Actions
  uploadPDF: (file: File) => Promise<void>;
  finalize: () => Promise<void>;
  pollStatus: () => Promise<void>;
  reset: () => void;

  // Computed
  canProceedToFinalize: boolean;
  isProcessing: boolean;
}

function mapPDFRecordToUploadState(pdf: PDFRecord): PDFUploadState {
  let status: PDFUploadState['status'];
  switch (pdf.status) {
    case 'COMPLETED':
      status = 'completed';
      break;
    case 'ERROR':
      status = 'error';
      break;
    case 'PROCESSING':
      status = 'processing';
      break;
    case 'PENDING_UPLOAD':
    default:
      status = 'pending';
      break;
  }

  return {
    filename: pdf.filename,
    status,
    progress: status === 'completed' ? 100 : 0,
    pdfId: pdf.pdfId,
    findingsExtracted: pdf.findingsCount,
    error: pdf.error,
  };
}

interface UseInsuranceReportOptions {
  initialReportId?: string;
  outputFormat?: OutputFormat;
}

export const useInsuranceReport = (
  options: UseInsuranceReportOptions = {}
): UseInsuranceReportReturn => {
  const { initialReportId, outputFormat } = options;
  const navigate = useNavigate();
  const [reportId, setReportId] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [uploadedPDFs, setUploadedPDFs] = useState<PDFUploadState[]>([]);
  const [reportStatus, setReportStatus] = useState<ReportStatus | null>(null);
  const [results, setResults] = useState<GetReportResponseCompleted | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Only show initializing spinner when loading an existing report
  const [isInitializing, setIsInitializing] = useState<boolean>(!!initialReportId);

  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isPollingPDFs, setIsPollingPDFs] = useState<boolean>(false);
  const reportIdRef = useRef<string | null>(null);
  // Track the current outputFormat via ref so lazy creation uses the latest value
  const outputFormatRef = useRef<OutputFormat>(outputFormat || 'standard');
  useEffect(() => {
    outputFormatRef.current = outputFormat || 'standard';
  }, [outputFormat]);
  // Guard against concurrent report creation
  const creatingReportRef = useRef<Promise<string> | null>(null);

  /**
   * Lazily create a new report (called on first PDF upload).
   * Returns the reportId.
   */
  const ensureReport = useCallback(async (): Promise<string> => {
    // Already have a report
    if (reportIdRef.current) return reportIdRef.current;

    // Another call is already creating the report — wait for it
    if (creatingReportRef.current) return creatingReportRef.current;

    const promise = (async () => {
      const response = await copilotApi.createInsuranceReport({
        outputFormat: outputFormatRef.current,
      });
      setReportId(response.reportId);
      reportIdRef.current = response.reportId;
      setReportStatus(response.status);
      setCurrentStep(0);
      // Update URL silently without triggering React Router re-render.
      // Using navigate() would change the key prop and remount the wizard,
      // killing the in-progress upload.
      window.history.replaceState(null, '', `/insurance-summaries/${response.reportId}`);
      return response.reportId;
    })();

    creatingReportRef.current = promise;
    try {
      return await promise;
    } finally {
      creatingReportRef.current = null;
    }
  }, []);

  /**
   * Initialize: load existing report from URL (if provided).
   * New reports are created lazily on first upload.
   */
  useEffect(() => {
    if (!initialReportId) return;

    const initReport = async () => {
      try {
        setError(null);

        // Load existing report
        const response = await copilotApi.getInsuranceReport(initialReportId);
        setReportId(response.reportId);
        setReportStatus(response.status);

        // Reconstruct PDF list from backend
        const restoredPDFs = response.pdfs.map(mapPDFRecordToUploadState);
        setUploadedPDFs(restoredPDFs);

        // Set correct wizard step based on status
        switch (response.status) {
          case 'UPLOADING':
            setCurrentStep(0);
            if (response.pdfs.some((p) => p.status === 'PROCESSING')) {
              setIsPollingPDFs(true);
            }
            break;
          case 'PROCESSING_SUMMARY':
            setCurrentStep(1);
            pollingIntervalRef.current = setInterval(() => {
              pollStatus();
            }, 2000);
            break;
          case 'COMPLETED':
            setResults(response as GetReportResponseCompleted);
            setCurrentStep(2);
            break;
          case 'ERROR':
            setError((response as GetReportResponseError).error || 'Report processing failed');
            setCurrentStep(0);
            break;
        }
      } catch (err: any) {
        setError(err.message || 'Failed to initialize report');
      } finally {
        setIsInitializing(false);
      }
    };
    initReport();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep reportIdRef in sync for use inside interval callbacks
  useEffect(() => {
    reportIdRef.current = reportId;
  }, [reportId]);

  /**
   * Step 2: Upload a single PDF via presigned S3 URL (supports parallel calls)
   *
   * Three-step flow:
   * 1. Get presigned upload URL from backend
   * 2. PUT raw PDF bytes directly to S3
   * 3. Confirm upload to trigger async processing
   */
  const uploadPDF = useCallback(async (file: File) => {
    // Lazily create the report on first upload (uses current outputFormat)
    const currentReportId = await ensureReport();

    // Add to local state immediately with uploading status
    const pdfState: PDFUploadState = {
      file,
      filename: file.name,
      status: 'uploading',
      progress: 0,
    };
    setUploadedPDFs((prev) => [...prev, pdfState]);

    try {
      // Step 1: Get presigned upload URL from backend
      const urlResponse = await copilotApi.getInsuranceReportUploadUrl(currentReportId, {
        filename: file.name,
        contentLength: file.size,
      });

      // Update state with pdfId
      setUploadedPDFs((prev) =>
        prev.map((p) =>
          p.file === file ? { ...p, pdfId: urlResponse.pdfId } : p
        )
      );

      // Step 2: Upload raw PDF directly to S3
      await copilotApi.uploadPDFToS3(urlResponse.uploadUrl, file);

      // Step 3: Confirm upload to backend (triggers processing)
      await copilotApi.confirmInsuranceReportPDFUpload(
        currentReportId,
        urlResponse.pdfId
      );

      // Update to processing status
      setUploadedPDFs((prev) =>
        prev.map((p) =>
          p.file === file
            ? {
                ...p,
                status: 'processing',
                pdfId: urlResponse.pdfId,
              }
            : p
        )
      );

      // Start polling for async processing
      setIsPollingPDFs(true);
    } catch (err: any) {
      // Update specific PDF in array to error
      setUploadedPDFs((prev) =>
        prev.map((p) =>
          p.file === file
            ? {
                ...p,
                status: 'error',
                error: err.message || 'Upload failed',
              }
            : p
        )
      );
      throw err;
    }
  }, [ensureReport]);

  /**
   * Poll for individual PDF processing statuses.
   * Uses a stable boolean dependency (isPollingPDFs) instead of the
   * uploadedPDFs array to avoid the interval being destroyed/recreated
   * on every state change.
   */
  useEffect(() => {
    if (!isPollingPDFs || !reportIdRef.current) {
      return;
    }

    const intervalId = setInterval(async () => {
      const currentReportId = reportIdRef.current;
      if (!currentReportId) return;

      try {
        const response = await copilotApi.getInsuranceReport(currentReportId);

        setUploadedPDFs((prev) => {
          let changed = false;
          const next = prev.map((localPdf) => {
            if (localPdf.status !== 'processing' || !localPdf.pdfId) return localPdf;

            const remotePdf = response.pdfs.find(
              (p) => p.pdfId === localPdf.pdfId
            );
            if (!remotePdf) return localPdf;

            if (remotePdf.status === 'COMPLETED') {
              changed = true;
              return {
                ...localPdf,
                status: 'completed' as const,
                findingsExtracted: remotePdf.findingsCount,
              };
            } else if (remotePdf.status === 'ERROR') {
              changed = true;
              return {
                ...localPdf,
                status: 'error' as const,
                error: remotePdf.error || 'Extraction failed',
              };
            }
            return localPdf;
          });

          if (!changed) return prev;

          // Stop polling when no more PDFs are processing
          const stillProcessing = next.some((pdf) => pdf.status === 'processing');
          if (!stillProcessing) {
            setIsPollingPDFs(false);
          }

          return next;
        });
      } catch (err) {
        console.error('Failed to poll PDF statuses:', err);
      }
    }, 3000);

    return () => {
      clearInterval(intervalId);
    };
  }, [isPollingPDFs]);

  /**
   * Step 3: Finalize report and start polling
   */
  const finalize = useCallback(async () => {
    if (!reportId) {
      throw new Error('No report created');
    }

    try {
      setError(null);
      await copilotApi.finalizeInsuranceReport(reportId);

      setReportStatus('PROCESSING_SUMMARY');
      setCurrentStep(1); // Move to processing step

      // Start polling
      pollingIntervalRef.current = setInterval(() => {
        pollStatus();
      }, 2000); // Poll every 2 seconds
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to finalize report';
      setError(errorMessage);
      throw err;
    }
  }, [reportId]);

  /**
   * Poll report status (called by interval in finalization step)
   */
  const pollStatus = useCallback(async () => {
    if (!reportId) {
      return;
    }

    try {
      const response: GetReportResponse = await copilotApi.getInsuranceReport(reportId);
      setReportStatus(response.status);

      if (response.status === 'COMPLETED') {
        // Stop polling
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        setResults(response);
        setCurrentStep(2); // Move to results step
      } else if (response.status === 'ERROR') {
        // Stop polling on error
        if (pollingIntervalRef.current) {
          clearInterval(pollingIntervalRef.current);
          pollingIntervalRef.current = null;
        }

        setError(response.error || 'Report processing failed');
      }
    } catch (err: any) {
      console.error('Failed to poll status:', err);
      // Don't set error state here, keep polling
    }
  }, [reportId]);

  /**
   * Reset to initial state and create a fresh report
   */
  const reset = useCallback(async () => {
    // Stop polling if active
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    setIsPollingPDFs(false);

    setReportId(null);
    reportIdRef.current = null;
    creatingReportRef.current = null;
    setUploadedPDFs([]);
    setReportStatus(null);
    setResults(null);
    setError(null);
    setCurrentStep(0);

    // Navigate to base route; report will be created lazily on next upload
    navigate('/insurance-summaries', { replace: true });
  }, [navigate]);

  // Computed values
  const canProceedToFinalize = uploadedPDFs.length > 0 &&
    uploadedPDFs.every((pdf) => pdf.status === 'completed' || pdf.status === 'error');

  const isProcessing = uploadedPDFs.some((pdf) =>
    pdf.status === 'uploading' || pdf.status === 'processing'
  );

  return {
    // State
    reportId,
    currentStep,
    uploadedPDFs,
    reportStatus,
    results,
    error,
    isInitializing,

    // Actions
    uploadPDF,
    finalize,
    pollStatus,
    reset,

    // Computed
    canProceedToFinalize,
    isProcessing,
  };
};
